// Define a type for the possible dashboard modes
export type DashboardMode = 'city' | 'super-admin' | 'consultant';

// A constant to check if the code is running in a browser environment
const isBrowser: boolean = typeof window !== 'undefined';

// Define the available dashboard modes using a readonly object for better type safety
// We use 'as const' to ensure that the object values are treated as literal types ('city', 'super-admin', etc.)
export const DashboardModes = Object.freeze({
  CITY: 'city',
  SUPER_ADMIN: 'super-admin',
  CONSULTANT: 'consultant'
}) as Readonly<Record<'CITY' | 'SUPER_ADMIN' | 'CONSULTANT', DashboardMode>>;

export const DASHBOARD_MODE_STORAGE_KEY: string = 'activeDashboardMode';
export const DASHBOARD_MODE_EVENT: string = 'dashboardModeUpdated';

// Type predicate to check if a value is one of the supported DashboardModes
const isSupportedMode = (value: string | null): value is DashboardMode => {
  // Check if the value is not null, is a string, and is included in the set of defined modes
  return value !== null && Object.values(DashboardModes).includes(value as DashboardMode);
};

// Dispatch a custom event to notify listeners about the mode change
const dispatchModeEvent = (mode: DashboardMode | null): void => {
  if (!isBrowser) {
    return;
  }

  const detail: { mode: DashboardMode | null } = { mode: mode || null };
  const eventName: string = DASHBOARD_MODE_EVENT;

  try {
    // Try creating CustomEvent with detail property
    window.dispatchEvent(new CustomEvent(eventName, { detail }));
  } catch (error) {
    // Fallback for environments where CustomEvent constructor doesn't support 'detail'
    const fallbackEvent = new Event(eventName) as Event & { detail: { mode: DashboardMode | null } };
    fallbackEvent.detail = detail;
    window.dispatchEvent(fallbackEvent);
  }
};

/**
 * Retrieves the stored dashboard mode from localStorage.
 * @returns The active DashboardMode or null if not set or unsupported.
 */
export const getDashboardMode = (): DashboardMode | null => {
  if (!isBrowser) {
    return null;
  }

  const storedValue: string | null = window.localStorage.getItem(DASHBOARD_MODE_STORAGE_KEY);
  // Use the type predicate to ensure the return type is correct
  return isSupportedMode(storedValue) ? storedValue : null;
};

/**
 * Sets the active dashboard mode in localStorage and optionally dispatches an event.
 * @param mode The DashboardMode to set, or null to clear it.
 * @param options Configuration options.
 * @param options.emitEvent If true, dispatch a custom event after setting/clearing the mode. Defaults to true.
 */
export const setDashboardMode = (mode: DashboardMode | null, { emitEvent = true }: { emitEvent?: boolean } = {}): void => {
  if (!isBrowser) {
    return;
  }

  // Check if a valid mode is provided and supported
  if (mode && isSupportedMode(mode)) {
    window.localStorage.setItem(DASHBOARD_MODE_STORAGE_KEY, mode);
    if (emitEvent) {
      dispatchModeEvent(mode);
    }
    return;
  }

  // If mode is null or unsupported, remove the item
  window.localStorage.removeItem(DASHBOARD_MODE_STORAGE_KEY);
  if (emitEvent) {
    dispatchModeEvent(null);
  }
};

/**
 * Utility object exporting all relevant dashboard mode constants and functions.
 */
export const dashboardModeUtils = {
  getDashboardMode,
  setDashboardMode,
  DashboardModes,
  DASHBOARD_MODE_EVENT,
  DASHBOARD_MODE_STORAGE_KEY
};

// Session flag: set when consultant selects a city in current session
export const CONSULTANT_SESSION_SELECTED_CITY_KEY: string = 'consultantSessionSelectedCity';
